import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import {
  apiClient,
  cacheAuthoritativeSession,
  clearCachedSession,
  isMembershipBackedSession,
} from "../pages/backend_url";
import "./ProtectedRoute.css";

const DEFAULT_RETRY_DELAY_MS = 5000;
const MAX_RETRY_DELAY_MS = 120000;

function retryDelayFromResponse(error) {
  const retryAfter = error?.response?.headers?.["retry-after"];
  if (!retryAfter) return DEFAULT_RETRY_DELAY_MS;

  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds)) {
    return Math.min(MAX_RETRY_DELAY_MS, Math.max(1000, seconds * 1000));
  }

  const retryAt = Date.parse(retryAfter);
  if (Number.isFinite(retryAt)) {
    return Math.min(MAX_RETRY_DELAY_MS, Math.max(1000, retryAt - Date.now()));
  }

  return DEFAULT_RETRY_DELAY_MS;
}

/**
 * Resolve the public entry route against the server session cookie.
 *
 * Browser storage is only a compatibility cache, so opening the CRM in a new
 * tab must ask the server whether the HttpOnly session is still valid before
 * deciding between the login screen and the authenticated application.
 */
export default function SessionEntry({
  children,
  onAuthenticated,
  // A restored HttpOnly session follows the same safe landing path as a new
  // login. Account Setup establishes the active workspace before users open
  // operational data modules.
  authenticatedPath = "/AccountSetup",
  membershipAuthenticatedPath = "/AccountSetup",
}) {
  const [checkNumber, setCheckNumber] = useState(0);
  const [status, setStatus] = useState("checking");
  const [destination, setDestination] = useState(authenticatedPath);
  const [retryDelayMs, setRetryDelayMs] = useState(DEFAULT_RETRY_DELAY_MS);

  const retry = useCallback(() => {
    setStatus("checking");
    setCheckNumber((value) => value + 1);
  }, []);

  // The login screen is public, so a temporary API outage must not replace it
  // with a dead-end page. Recheck quietly and immediately when connectivity
  // returns; protected CRM content remains inaccessible until a 200 response.
  useEffect(() => {
    if (status !== "error") return undefined;
    const timer = window.setTimeout(retry, retryDelayMs);
    window.addEventListener("online", retry);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("online", retry);
    };
  }, [retry, retryDelayMs, status]);

  useEffect(() => {
    const abortController = new AbortController();
    let active = true;

    const resolveSession = async () => {
      try {
        const response = await apiClient.get("/auth/session", {
          headers: { "Cache-Control": "no-store" },
          signal: abortController.signal,
          timeout: 8000,
        });
        const cachedUser = cacheAuthoritativeSession(response.data);

        if (active) {
          setDestination(
            isMembershipBackedSession(cachedUser)
              ? membershipAuthenticatedPath
              : authenticatedPath
          );
          onAuthenticated?.(cachedUser);
          setStatus("authenticated");
        }
      } catch (error) {
        if (!active || error?.code === "ERR_CANCELED") return;

        if (error?.response?.status === 401) {
          // Only an authoritative unauthenticated response is allowed to
          // expose the login form and remove the compatibility cache.
          clearCachedSession();
          setStatus("login");
          return;
        }

        // A network error, throttling response, or server failure cannot prove
        // that the session is absent. Keep the entry route closed and retryable.
        setRetryDelayMs(retryDelayFromResponse(error));
        setStatus("error");
      }
    };

    resolveSession();

    return () => {
      active = false;
      abortController.abort();
    };
  }, [authenticatedPath, checkNumber, membershipAuthenticatedPath, onAuthenticated]);

  if (status === "authenticated") {
    return <Navigate to={destination} replace />;
  }

  // The public login remains mounted during session checks and retries. This
  // keeps the entry route useful during an API outage and preserves anything
  // the user has typed, while protected CRM modules remain fail-closed.
  return (
    <>
      {status === "checking" && (
        <div
          className="session-check-indicator"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <span className="protected-route-spinner" aria-hidden="true" />
          <span className="session-check-indicator__label">
            Checking for an existing secure session...
          </span>
        </div>
      )}

      {status === "error" && (
        <div className="session-service-banner" role="alert" aria-live="polite">
          <div>
            <strong>CRM service is reconnecting</strong>
            <span>
              The login page remains available. Sign-in and existing-session recovery will resume automatically.
            </span>
          </div>
          <button type="button" onClick={retry}>
            Retry now
          </button>
        </div>
      )}

      {children}
    </>
  );
}
